import React, { useRef, useEffect, useCallback } from 'react';
import { Skeleton } from '@/ui';

export interface SearchResult {
  id: string;
  type: 'report' | 'appointment' | 'inspection';
  title: string;
  subtitle: string;
  href: string;
}

export interface SearchDropdownHandle {
  focusItem: (idx: number) => void;
}

interface SearchDropdownProps {
  query: string;
  results: SearchResult[];
  loading: boolean;
  activeIndex: number;
  onSelect: (result: SearchResult) => void;
  onHover: (index: number) => void;
  onReturnToInput: () => void;
}

export const SearchDropdown = React.forwardRef<SearchDropdownHandle, SearchDropdownProps>(
  function SearchDropdown({ query, results, loading, activeIndex, onSelect, onHover, onReturnToInput }, ref) {
    const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

    // Clear stale refs when result count changes
    useEffect(() => {
      buttonRefs.current = new Array(results.length).fill(null);
    }, [results.length]);

    React.useImperativeHandle(ref, () => ({
      focusItem: (idx: number) => {
        buttonRefs.current[idx]?.focus();
      },
    }));

    const handleButtonKeyDown = useCallback((e: React.KeyboardEvent, flatIdx: number) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = buttonRefs.current[flatIdx + 1];
        if (next) {
          next.focus();
          onHover(flatIdx + 1);
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (flatIdx > 0) {
          buttonRefs.current[flatIdx - 1]?.focus();
          onHover(flatIdx - 1);
        } else {
          onReturnToInput();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onReturnToInput();
      }
    }, [onHover, onReturnToInput]);

    const apptResults = results.filter(r => r.type === 'appointment');
    const inspResults = results.filter(r => r.type === 'inspection');
    const reportResults = results.filter(r => r.type === 'report');
    const hasResults = results.length > 0;

    return (
      <div className="absolute left-0 right-0 top-full mt-1 bg-surface-1 border border-border-subtle rounded-xl shadow-2xl z-50 overflow-hidden">
        <div className="max-h-80 overflow-y-auto">
          {loading && !hasResults ? (
            <div className="p-3 space-y-2">
              {[0, 1, 2].map(i => (
                <div key={i} className="flex items-center gap-3 px-1 py-1.5">
                  <Skeleton width="w-4" height="h-4" shape="circle" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton width="w-3/5" height="h-4" />
                    <Skeleton width="w-2/5" height="h-3" />
                  </div>
                </div>
              ))}
            </div>
          ) : !hasResults ? (
            <p className="px-4 py-5 text-body-sm text-ink-subtle text-center">
              No results for "{query}"
            </p>
          ) : (
            <div className="py-1">
              {apptResults.length > 0 && (
                <ResultGroup
                  label="Appointments"
                  results={apptResults}
                  globalOffset={0}
                  activeIndex={activeIndex}
                  buttonRefs={buttonRefs}
                  onSelect={onSelect}
                  onHover={onHover}
                  onKeyDown={handleButtonKeyDown}
                />
              )}
              {inspResults.length > 0 && (
                <ResultGroup
                  label="Inspections"
                  results={inspResults}
                  globalOffset={apptResults.length}
                  activeIndex={activeIndex}
                  buttonRefs={buttonRefs}
                  onSelect={onSelect}
                  onHover={onHover}
                  onKeyDown={handleButtonKeyDown}
                />
              )}
              {reportResults.length > 0 && (
                <ResultGroup
                  label="Reports"
                  results={reportResults}
                  globalOffset={apptResults.length + inspResults.length}
                  activeIndex={activeIndex}
                  buttonRefs={buttonRefs}
                  onSelect={onSelect}
                  onHover={onHover}
                  onKeyDown={handleButtonKeyDown}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);

interface ResultGroupProps {
  label: string;
  results: SearchResult[];
  globalOffset: number;
  activeIndex: number;
  buttonRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>;
  onSelect: (r: SearchResult) => void;
  onHover: (i: number) => void;
  onKeyDown: (e: React.KeyboardEvent, flatIdx: number) => void;
}

const ResultGroup: React.FC<ResultGroupProps> = ({
  label, results, globalOffset, activeIndex, buttonRefs, onSelect, onHover, onKeyDown,
}) => (
  <div>
    <p className="px-3 pt-2 pb-0.5 text-overline text-ink-subtle">{label}</p>
    {results.map((r, i) => {
      const idx = globalOffset + i;
      const isActive = idx === activeIndex;
      return (
        <button
          key={r.id}
          ref={(el) => { buttonRefs.current[idx] = el; }}
          type="button"
          className={[
            'w-full flex items-center gap-3 px-3 py-2 text-left transition-colors',
            'focus-visible:outline-none focus-visible:bg-brand/10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand/30',
            isActive ? 'bg-brand/10 border-l-2 border-brand pl-[10px]' : 'hover:bg-surface-2',
          ].join(' ')}
          onMouseEnter={() => onHover(idx)}
          onClick={() => onSelect(r)}
          onKeyDown={(e) => onKeyDown(e, idx)}
        >
          <span className="shrink-0 text-ink-subtle">
            {r.type === 'appointment' && <CalendarIcon />}
            {r.type === 'inspection' && <ClipboardIcon />}
            {r.type === 'report' && <FileIcon />}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-body-sm font-medium text-ink truncate">{r.title}</p>
            <p className="text-caption text-ink-muted truncate">{r.subtitle}</p>
          </div>
        </button>
      );
    })}
  </div>
);

const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ClipboardIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
  </svg>
);

const FileIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);
